import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Users, Plus, Upload, Mail, Send, Trash2, Search, Loader2,
    CheckCircle2, MessageCircle, Ban, X, Edit3, Eye, Phone,
} from "lucide-react";
import api from "@/lib/api";
import ProspectingPanel from "@/components/ProspectingPanel";
import PendingListingsPanel from "@/components/PendingListingsPanel";
import LeadFinderPanel from "@/components/LeadFinderPanel";
import { OutreachMailEditor, OutreachVideosEditor } from "@/components/OutreachEditor";
import { CallNowDialog } from "@/components/CallNowDialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Admin · Leads — prospect database for cold outreach to brokers.
 *
 * Flow:
 * 1. Add leads (one-by-one form or bulk paste of "email, name" rows).
 * 2. From a row: click "Stuur intro" → backend queues a branded mail in
 *    /admin/mails → admin opens that draft, optionally personalises the
 *    intro line, hits Verzenden.
 * 3. The lead row stamps `last_intro_sent_at` and moves `new → contacted`.
 *
 * Replies + drip automation are Phase 2 — for now we record manual status
 * changes only ("Geantwoord", "Uitgeschreven").
 */
const STATUS_META = {
    new: { label: "Nieuw", color: "text-amber-700", bg: "bg-amber-500/10 border-amber-500/30" },
    contacted: { label: "Gecontacteerd", color: "text-blue-700", bg: "bg-blue-500/10 border-blue-500/30" },
    replied: { label: "Geantwoord", color: "text-emerald-700", bg: "bg-emerald-500/10 border-emerald-500/30" },
    unsubscribed: { label: "Uitgeschreven", color: "text-rose-700", bg: "bg-rose-500/10 border-rose-500/30" },
};

export default function AdminLeads() {
    const navigate = useNavigate();
    const [leads, setLeads] = useState([]);
    const [counts, setCounts] = useState({ new: 0, contacted: 0, replied: 0, unsubscribed: 0, total: 0 });
    const [statusFilter, setStatusFilter] = useState("all");
    const [engagementFilter, setEngagementFilter] = useState("all"); // all | warm | opened | clicked
    const [audienceFilter, setAudienceFilter] = useState("all"); // all | agency | photographer
    const [callList, setCallList] = useState([]);

    useEffect(() => {
        if (audienceFilter !== "tenerife") return;
        api.get("/admin/leads/call-list", { params: { audience: "tenerife" } })
            .then(({ data }) => setCallList(data.leads || []))
            .catch(() => {});
    }, [audienceFilter]);

    const toggleCalled = async (id) => {
        try {
            const { data } = await api.post(`/admin/leads/${id}/toggle-called`);
            setCallList((l) => l.map((x) => (x.id === id ? { ...x, called_at: data.called ? new Date().toISOString() : null } : x)));
        } catch {
            toast.error("Markeren mislukt");
        }
    };
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewHtml, setPreviewHtml] = useState(null);
    const [previewStep, setPreviewStep] = useState(1);
    const [previewAudience, setPreviewAudience] = useState("agency");
    const [previewSubject, setPreviewSubject] = useState("");
    const [drawerTab, setDrawerTab] = useState("preview"); // preview | edit | videos
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [callTarget, setCallTarget] = useState(null);
    const [stats, setStats] = useState(null);

    const loadStats = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/leads/stats");
            setStats(data);
        } catch (_) { /* silent */ }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter !== "all") params.set("status", statusFilter);
            if (engagementFilter !== "all") params.set("engagement", engagementFilter);
            if (audienceFilter !== "all") params.set("lead_type", audienceFilter);
            if (search.trim()) params.set("q", search.trim());
            const { data } = await api.get(`/admin/leads?${params.toString()}`);
            setLeads(data.leads || []);
            setCounts(data.counts || {});
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kon leads niet ophalen");
        } finally {
            setLoading(false);
        }
    }, [statusFilter, engagementFilter, audienceFilter, search]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadStats(); }, [loadStats]);

    const queueIntro = async (lead) => {
        try {
            const { data } = await api.post(`/admin/leads/${lead.id}/queue-intro`);
            if (data.ok) {
                toast.success(`Intro-mail klaargezet voor ${lead.email}`);
                await load();
                // Navigate to the Mails page so the admin can immediately
                // preview + personalise + send. Hash anchor lets a future
                // AdminMails update auto-open that specific row.
                navigate(`/admin/mails#${data.mail_id}`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aanmaken mail mislukt");
        }
    };

    const loadPreview = async (step = 1, aud = previewAudience) => {
        setPreviewHtml(null);
        setPreviewStep(step);
        try {
            const { data } = await api.get(
                `/admin/leads/intro/preview?name=Marc&company=Immo+De+Vries&language=nl&step=${step}&audience=${aud}`
            );
            setPreviewHtml(data.body_html);
            setPreviewSubject(data.subject || "");
        } catch (e) {
            toast.error("Kon preview niet laden");
        }
    };

    const showPreview = async () => {
        setPreviewOpen(true);
        setDrawerTab("preview");
        await loadPreview(1);
    };

    const remove = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/admin/leads/${deleteTarget.id}`);
            toast.success("Lead verwijderd");
            setDeleteTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Verwijderen mislukt");
        }
    };

    const setStatus = async (lead, status) => {
        try {
            await api.put(`/admin/leads/${lead.id}`, { status });
            toast.success("Status bijgewerkt");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kon status niet wijzigen");
        }
    };

    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-950" data-testid="admin-leads-page">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#E5A93D] mb-2 inline-flex items-center gap-2">
                            <Users className="h-3.5 w-3.5" /> Admin · Leads
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Prospecten.</h1>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={showPreview}
                            data-testid="leads-preview-mail"
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-zinc-200 text-zinc-800 hover:bg-zinc-100 text-sm"
                        >
                            <Eye className="h-4 w-4" /> Bekijk mail-template
                        </button>
                        <button
                            type="button"
                            onClick={() => setBulkOpen(true)}
                            data-testid="leads-bulk-open"
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-zinc-200 text-zinc-800 hover:bg-zinc-100 text-sm"
                        >
                            <Upload className="h-4 w-4" /> Bulk import
                        </button>
                        <button
                            type="button"
                            onClick={() => setAddOpen(true)}
                            data-testid="leads-add-open"
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-sm transition active:scale-[0.98]"
                        >
                            <Plus className="h-4 w-4" /> Nieuwe lead
                        </button>
                    </div>
                </div>
                <p className="text-sm text-zinc-500 max-w-2xl mb-8">
                    Database van potentiële klanten. Verstuur een gepersonaliseerde intro-mail in jouw branding —
                    je past de openingszin aan en klikt verzenden. Status wordt automatisch bijgewerkt.
                </p>

                {/* Outreach funnel widget — sent today vs daily cap, 7-day
                    sparkline, reply-rate and ETA-to-finish. */}
                {stats && <LeadsStatsWidget stats={stats} onRefresh={() => { loadStats(); load(); }} />}

                {/* AI Prospecting Engine — manual scan + draft review */}
                <ProspectingPanel />
                <PendingListingsPanel />

                {/* Google Maps lead finder — grow the FR/EN lead pool */}
                <LeadFinderPanel onLeadsAdded={() => load()} />

                {/* Status tabs */}
                <div className="flex flex-wrap gap-2 mb-2">
                    {[
                        { key: "all", label: "Alle", count: counts.total },
                        { key: "new", label: "Nieuw", count: counts.new },
                        { key: "contacted", label: "Gecontacteerd", count: counts.contacted },
                        { key: "replied", label: "Geantwoord", count: counts.replied },
                        { key: "unsubscribed", label: "Uitgeschreven", count: counts.unsubscribed },
                    ].map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setStatusFilter(t.key)}
                            data-testid={`leads-filter-${t.key}`}
                            className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-sm text-xs font-bold uppercase tracking-wider transition ${
                                statusFilter === t.key
                                    ? "bg-white text-zinc-950"
                                    : "bg-white border border-zinc-200 text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100"
                            }`}
                        >
                            {t.label}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${statusFilter === t.key ? "bg-black/15" : "bg-white/10"}`}>
                                {t.count ?? 0}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Doelgroep filter — makelaars vs vastgoedfotografen */}
                <div className="flex flex-wrap gap-2 mb-2 items-center">
                    <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold mr-1">Doelgroep:</span>
                    {[
                        { key: "all", label: "Alle", count: counts.total },
                        { key: "agency", label: "🏠 Makelaars", count: counts.agency },
                        { key: "photographer", label: "📸 Fotografen", count: counts.photographer },
                        { key: "tenerife", label: "🌴 Tenerife", count: counts.tenerife },
                    ].map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setAudienceFilter(t.key)}
                            data-testid={`leads-audience-filter-${t.key}`}
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-sm text-[11px] font-bold uppercase tracking-wider transition ${
                                audienceFilter === t.key
                                    ? "bg-[#E5A93D] text-black"
                                    : "bg-white border border-zinc-200 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100"
                            }`}
                        >
                            {t.label}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${audienceFilter === t.key ? "bg-black/15" : "bg-white/10"}`}>
                                {t.count ?? 0}
                            </span>
                        </button>
                    ))}
                </div>

                {/* 🌴 Tenerife bellijst — warme leads (geklikt/geantwoord) bovenaan */}
                {audienceFilter === "tenerife" && callList.length > 0 && (
                    <div className="bg-white border-2 border-[#E5A93D]/60 rounded-md p-4 mb-4" data-testid="tenerife-call-list">
                        <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-bold mb-3">
                            📞 Bellijst — warme Tenerife-leads ({callList.length}) · plan je demo's vóór 23 november
                        </p>
                        <div className="space-y-2">
                            {callList.map((l) => (
                                <div key={l.id} className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-sm border-b border-zinc-100 pb-2 last:border-0 last:pb-0 ${l.called_at ? "opacity-50" : ""}`} data-testid={`call-list-row-${l.id}`}>
                                    <span className="font-bold text-zinc-950 min-w-[160px]">{l.company || l.name}</span>
                                    {l.status === "replied" ? (
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-green-100 text-green-700">💬 Geantwoord</span>
                                    ) : (
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-amber-100 text-amber-700">🔗 Geklikt{l.total_click_count > 1 ? ` ×${l.total_click_count}` : ""}</span>
                                    )}
                                    <span className="text-xs text-zinc-500">{(l.replied_at || l.last_click_at || "").slice(0, 10)}</span>
                                    {l.phone && <a href={`tel:${(() => { const d = String(l.phone).replace(/[^\d+]/g, ""); if (d.startsWith("+")) return d; if (d.startsWith("00")) return `+${d.slice(2)}`; return d.startsWith("0") ? d : `+${d}`; })()}`} className="text-[#E5A93D] font-bold hover:underline inline-flex items-center gap-1"><Phone className="h-3 w-3" />{l.phone}</a>}
                                    <a href={`mailto:${l.email}`} className="text-xs text-zinc-500 hover:text-zinc-950 hover:underline">{l.email}</a>
                                    {l.city && <span className="text-xs text-zinc-400">{l.city}</span>}
                                    <button
                                        type="button"
                                        onClick={() => toggleCalled(l.id)}
                                        className={`ml-auto text-[11px] font-bold px-2.5 h-7 rounded-sm transition ${l.called_at ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-600 hover:bg-[#E5A93D] hover:text-black"}`}
                                        data-testid={`call-list-called-${l.id}`}
                                    >
                                        {l.called_at ? "✓ Gebeld" : "Markeer gebeld"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {audienceFilter === "tenerife" && callList.length === 0 && (
                    <p className="text-xs text-zinc-500 mb-4" data-testid="tenerife-call-list-empty">📞 Bellijst: nog geen Tenerife-leads die klikten of antwoordden — zodra dat gebeurt verschijnen ze hier bovenaan.</p>
                )}

                {/* Engagement filter — surfaces warm prospects (opened / clicked the mail) */}
                <div className="flex flex-wrap gap-2 mb-4 items-center">
                    <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold mr-1">Activiteit:</span>
                    {[
                        { key: "all", label: "Alle", count: null },
                        { key: "warm", label: "🔥 Warm", count: counts.warm, hot: true },
                        { key: "opened", label: "👁 Geopend", count: counts.opened },
                        { key: "clicked", label: "🖱 Geklikt", count: counts.clicked },
                        { key: "video", label: "🎬 Video bekeken", count: counts.video, hot: true },
                    ].map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setEngagementFilter(t.key)}
                            data-testid={`leads-engagement-filter-${t.key}`}
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-sm text-[11px] font-bold uppercase tracking-wider transition ${
                                engagementFilter === t.key
                                    ? (t.hot ? "bg-emerald-500 text-black" : "bg-zinc-100 text-black")
                                    : "bg-white border border-zinc-200 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100"
                            }`}
                        >
                            {t.label}
                            {t.count !== null && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${engagementFilter === t.key ? "bg-black/15" : "bg-white/10"}`}>
                                    {t.count ?? 0}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Search */}
                <div className="relative mb-6 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Zoek op e-mail, naam, kantoor of stad…"
                        data-testid="leads-search-input"
                        className="w-full h-10 bg-white border border-zinc-200 focus:border-[#E5A93D]/60 outline-none rounded-sm pl-9 pr-3 text-sm placeholder:text-zinc-400"
                    />
                </div>

                {/* Table */}
                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> Laden…</div>
                ) : leads.length === 0 ? (
                    <div className="text-center py-20 border border-zinc-200 rounded-sm bg-white">
                        <Mail className="h-10 w-10 mx-auto text-zinc-400 mb-4" />
                        <p className="text-zinc-500 text-sm mb-2">Nog geen leads in deze weergave.</p>
                        <p className="text-zinc-400 text-xs">Voeg je eerste prospect toe via &quot;Nieuwe lead&quot; of importeer een lijst.</p>
                    </div>
                ) : (
                    <div className="border border-zinc-200 rounded-sm overflow-hidden" data-testid="leads-table">
                        {/* Desktop header — hidden on mobile (cards take over) */}
                        <div className="hidden md:grid bg-white border-b border-zinc-200 px-4 py-2.5 grid-cols-12 gap-3 text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold">
                            <div className="col-span-3">Naam / Email</div>
                            <div className="col-span-2">Kantoor</div>
                            <div className="col-span-1">Stad</div>
                            <div className="col-span-2">Telefoon / Website</div>
                            <div className="col-span-1">Status</div>
                            <div className="col-span-1">Activiteit</div>
                            <div className="col-span-2 text-right">Acties</div>
                        </div>
                        {leads.map((lead) => (
                            <div
                                key={lead.id}
                                data-testid={`lead-row-${lead.id}`}
                                className="border-b border-zinc-100 hover:bg-zinc-100 px-4 py-3 md:grid md:grid-cols-12 md:gap-3 md:items-center md:text-sm flex flex-col gap-2"
                            >
                                {/* Identity */}
                                <div className="md:col-span-3 min-w-0">
                                    <div className="font-medium text-zinc-950 truncate">
                                        {lead.lead_type === "photographer" && <span title="Vastgoedfotograaf" className="mr-1">📸</span>}
                                        {lead.lead_type === "tenerife" && <span title="Tenerife makelaar" className="mr-1">🌴</span>}
                                        {lead.name || "—"}
                                    </div>
                                    <div className="text-xs text-zinc-500 truncate">{lead.email}</div>
                                </div>

                                {/* Mobile: combined meta row · Desktop: individual cols */}
                                <div className="md:col-span-2 text-zinc-600 truncate text-xs md:text-sm">
                                    <span className="md:hidden text-zinc-500 mr-1">Kantoor:</span>
                                    {lead.company || <span className="text-zinc-400">—</span>}
                                </div>
                                <div className="md:col-span-1 text-zinc-500 truncate text-xs md:text-sm">
                                    <span className="md:hidden text-zinc-500 mr-1">Stad:</span>
                                    {lead.city || <span className="text-zinc-400">—</span>}
                                </div>
                                <div className="md:col-span-2 min-w-0 text-xs md:text-sm">
                                    <div className="text-zinc-600 truncate">
                                        <span className="md:hidden text-zinc-500 mr-1">Tel:</span>
                                        {lead.phone || <span className="text-zinc-400">—</span>}
                                    </div>
                                    {lead.website && (
                                        <a
                                            href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`}
                                            target="_blank" rel="noreferrer"
                                            className="text-[11px] text-[#E5A93D] hover:underline truncate block"
                                        >
                                            {lead.website.replace(/^https?:\/\//, "")}
                                        </a>
                                    )}
                                </div>

                                {/* Status + verstuurd row */}
                                <div className="md:col-span-1 flex items-center gap-2 md:block">
                                    <span className="md:hidden text-[10px] uppercase tracking-wider text-zinc-500">Status:</span>
                                    <StatusPill status={lead.status} onChange={(s) => setStatus(lead, s)} />
                                </div>
                                <div className="md:col-span-1 text-[11px] text-zinc-500 flex items-center gap-2 md:block">
                                    <span className="md:hidden uppercase tracking-wider text-zinc-500">Activiteit:</span>
                                    <div className="flex flex-col gap-0.5">
                                        <span>
                                            {lead.last_intro_sent_at
                                                ? new Date(lead.last_intro_sent_at).toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "2-digit" })
                                                : <span className="text-zinc-400">—</span>}
                                        </span>
                                        <EngagementBadges lead={lead} />
                                    </div>
                                </div>

                                {/* Actions — full-width row on mobile, right-aligned on desktop */}
                                <div className="md:col-span-2 flex items-center justify-end gap-1 pt-1 md:pt-0">
                                    {(lead.videos_clicked || []).length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setCallTarget(lead)}
                                            data-testid={`lead-call-now-${lead.id}`}
                                            title="Warme lead — bekeek je video. Bel nu!"
                                            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-sm bg-emerald-500 text-black hover:bg-emerald-400 text-[11px] font-bold transition animate-pulse hover:animate-none"
                                        >
                                            <Phone className="h-3 w-3" /> Bel nu
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => queueIntro(lead)}
                                        disabled={lead.status === "unsubscribed"}
                                        data-testid={`lead-send-intro-${lead.id}`}
                                        title={lead.status === "unsubscribed" ? "Uitgeschreven — niet meer mailen" : "Stuur intro mail"}
                                        className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-sm bg-[#E5A93D]/15 border border-[#E5A93D]/40 text-[#E5A93D] hover:bg-[#E5A93D]/25 text-[11px] font-bold transition disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <Send className="h-3 w-3" /> Intro
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEditTarget(lead)}
                                        data-testid={`lead-edit-${lead.id}`}
                                        title="Bewerken"
                                        className="inline-flex items-center justify-center h-8 w-8 rounded-sm border border-zinc-200 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 transition"
                                    >
                                        <Edit3 className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDeleteTarget(lead)}
                                        data-testid={`lead-delete-${lead.id}`}
                                        title="Verwijderen"
                                        className="inline-flex items-center justify-center h-8 w-8 rounded-sm border border-zinc-200 text-zinc-500 hover:text-rose-700 hover:border-rose-500/40 hover:bg-rose-500/5 transition"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <p className="mt-6 text-xs text-zinc-500">
                    Verzonden mails verschijnen in <Link to="/admin/mails" className="text-[#E5A93D] hover:underline">/admin/mails</Link> ·
                    Configureer je SMTP-mailbox in <Link to="/admin/mail-settings" className="text-[#E5A93D] hover:underline">/admin/mail-settings</Link>.
                </p>
            </div>

            {addOpen && <AddLeadModal onClose={() => { setAddOpen(false); load(); }} />}
            {bulkOpen && <BulkLeadsModal onClose={() => { setBulkOpen(false); load(); }} />}
            {editTarget && (
                <EditLeadModal
                    lead={editTarget}
                    onClose={() => { setEditTarget(null); load(); }}
                />
            )}
            {callTarget && <CallNowDialog lead={callTarget} onClose={() => setCallTarget(null)} />}

            {/* Preview drawer for the intro mail template */}
            {previewOpen && (
                <div className="fixed inset-0 z-50 flex" onClick={() => setPreviewOpen(false)}>
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
                    <div
                        className="relative ml-auto w-full max-w-[680px] h-full bg-white border-l border-zinc-200 shadow-2xl flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
                            <div className="min-w-0 flex-1 pr-3">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-[#E5A93D] mb-1 font-bold">
                                    {drawerTab === "preview" && "Voorbeeld"}
                                    {drawerTab === "edit" && "Bewerken"}
                                    {drawerTab === "videos" && "Video's"}
                                </p>
                                <h2 className="font-display text-xl font-bold">
                                    {drawerTab === "videos" && "Videopagina's beheren"}
                                    {drawerTab !== "videos" && previewStep === 1 && "Intro-mail (Dag 0)"}
                                    {drawerTab !== "videos" && previewStep === 2 && "Follow-up #1 (Dag +5) — \"Stuur me 1 foto\""}
                                    {drawerTab !== "videos" && previewStep === 3 && "Follow-up #2 (Dag +12) — Break-up"}
                                    {drawerTab !== "videos" && previewStep === 4 && "Hot lead — klikte maar registreerde niet"}
                                </h2>
                                {drawerTab === "preview" && previewSubject && (
                                    <p className="text-xs text-zinc-500 mt-1 truncate">
                                        Onderwerp: <span className="text-zinc-800">{previewSubject}</span>
                                    </p>
                                )}
                            </div>
                            <button onClick={() => setPreviewOpen(false)} className="text-zinc-500 hover:text-zinc-950 shrink-0" data-testid="leads-preview-close">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        {/* Tab bar: Voorbeeld / Bewerken / Video's */}
                        <div className="px-5 pt-4 pb-3 border-b border-zinc-100 flex flex-wrap items-center gap-1.5 bg-white">
                            {[
                                { key: "preview", label: "Voorbeeld" },
                                { key: "edit", label: "Bewerken" },
                                { key: "videos", label: "Video's" },
                            ].map((t) => (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => {
                                        setDrawerTab(t.key);
                                        if (t.key === "preview") loadPreview(previewStep);
                                    }}
                                    data-testid={`drawer-tab-${t.key}`}
                                    className={`text-xs px-3 h-8 rounded-sm border transition-colors ${
                                        drawerTab === t.key
                                            ? "bg-white text-zinc-950 border-zinc-300 font-semibold"
                                            : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                                    }`}
                                >
                                    {t.label}
                                </button>
                            ))}
                            {drawerTab !== "videos" && <span className="w-px h-5 bg-zinc-100 mx-1.5" />}
                            {drawerTab !== "videos" && [
                                { key: "agency", label: "🏠 Makelaars" },
                                { key: "photographer", label: "📸 Fotografen" },
                                { key: "tenerife", label: "🌴 Tenerife" },
                            ].map((a) => (
                                <button
                                    key={a.key}
                                    type="button"
                                    onClick={() => {
                                        setPreviewAudience(a.key);
                                        if (drawerTab === "preview") loadPreview(previewStep, a.key);
                                    }}
                                    data-testid={`preview-audience-${a.key}`}
                                    className={`text-xs px-3 h-8 rounded-sm border transition-colors ${
                                        previewAudience === a.key
                                            ? "bg-zinc-900 text-white border-zinc-900 font-semibold"
                                            : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                                    }`}
                                >
                                    {a.label}
                                </button>
                            ))}
                            {drawerTab !== "videos" && <span className="w-px h-5 bg-zinc-100 mx-1.5" />}
                            {drawerTab !== "videos" && [
                                { step: 1, label: "Intro" },
                                { step: 2, label: "Follow-up #1" },
                                { step: 3, label: "Follow-up #2" },
                                { step: 4, label: "Hot lead" },
                            ].map((s) => (
                                <button
                                    key={s.step}
                                    type="button"
                                    onClick={() => {
                                        setPreviewStep(s.step);
                                        if (drawerTab === "preview") loadPreview(s.step);
                                    }}
                                    data-testid={`preview-step-${s.step}-btn`}
                                    className={`text-xs px-3 h-8 rounded-sm border transition-colors ${
                                        previewStep === s.step
                                            ? "bg-[#E5A93D] text-black border-[#E5A93D] font-semibold"
                                            : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                                    }`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                        <div className="flex-1 overflow-y-auto bg-zinc-50">
                            {drawerTab === "edit" && (
                                <OutreachMailEditor key={`${previewStep}-${previewAudience}`} step={previewStep} audience={previewAudience} onSaved={() => { /* preview refreshes on tab switch */ }} />
                            )}
                            {drawerTab === "videos" && <OutreachVideosEditor />}
                            {drawerTab === "preview" && (
                                <div className="p-4 h-full">
                                    {previewHtml ? (
                                        <iframe
                                            title="lead-intro-preview"
                                            srcDoc={previewHtml}
                                            className="w-full h-full min-h-[700px] bg-white rounded-sm border border-zinc-200"
                                            sandbox=""
                                            data-testid="leads-preview-iframe"
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center h-full text-zinc-500 text-sm">
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Voorbeeld laden…
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Lead verwijderen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Je verwijdert {deleteTarget?.email}. Verzonden mails blijven bewaard in het mails-archief.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="lead-delete-cancel">Annuleren</AlertDialogCancel>
                        <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-500" data-testid="lead-delete-confirm">
                            Verwijderen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}


function EngagementBadges({ lead }) {
    const opens = Number(lead.total_open_count || 0);
    const clicks = Number(lead.total_click_count || 0);
    const lastOpen = lead.last_open_at ? new Date(lead.last_open_at) : null;
    const lastClick = lead.last_click_at ? new Date(lead.last_click_at) : null;
    if (!opens && !clicks) {
        return <span className="text-zinc-400 text-[10px]">geen activiteit</span>;
    }
    const fmt = (d) => d ? d.toLocaleString("nl-BE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
    return (
        <span className="inline-flex items-center gap-1.5 text-[10px]" data-testid={`lead-engagement-${lead.id}`}>
            {opens > 0 && (
                <span
                    title={`Geopend ${opens}× · laatst ${fmt(lastOpen)}`}
                    data-testid={`lead-opens-${lead.id}`}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm bg-sky-500/10 border border-sky-500/30 text-sky-700 font-bold"
                >
                    👁 {opens}
                </span>
            )}
            {clicks > 0 && (
                <span
                    title={`Geklikt ${clicks}× · laatst ${fmt(lastClick)}${lead.last_clicked_url ? "\n→ " + lead.last_clicked_url : ""}`}
                    data-testid={`lead-clicks-${lead.id}`}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm bg-emerald-500/15 border border-emerald-500/40 text-emerald-700 font-bold"
                >
                    🖱 {clicks}
                </span>
            )}
            {(lead.videos_clicked || []).map((slug) => (
                <span
                    key={slug}
                    title={`Bekeek videopagina ${slug}${lead.last_video_click_at ? " · laatst " + fmt(new Date(lead.last_video_click_at)) : ""} — warme lead, bellen!`}
                    data-testid={`lead-video-${lead.id}-${slug}`}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm bg-[#E5A93D]/15 border border-[#E5A93D]/50 text-[#E5A93D] font-bold"
                >
                    🎬 {slug === "demo-retouch" ? "Demo" : slug.replace("video-", "V")}
                </span>
            ))}
        </span>
    );
}


function StatusPill({ status, onChange }) {
    const meta = STATUS_META[status] || STATUS_META.new;
    return (
        <div className="relative inline-block">
            <select
                value={status}
                onChange={(e) => onChange(e.target.value)}
                data-testid="lead-status-select"
                className={`appearance-none cursor-pointer text-[10px] uppercase tracking-wider font-bold rounded-sm px-2 py-1 pr-5 border ${meta.bg} ${meta.color} focus:outline-none`}
            >
                {Object.entries(STATUS_META).map(([key, m]) => (
                    <option key={key} value={key} className="bg-white text-zinc-950">{m.label}</option>
                ))}
            </select>
        </div>
    );
}

function AddLeadModal({ onClose }) {
    const [form, setForm] = useState({
        email: "", name: "", company: "", phone: "", website: "", city: "", source: "", language: "nl",
    });
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!form.email.trim()) return toast.error("E-mail is verplicht");
        setSaving(true);
        try {
            await api.post("/admin/leads", form);
            toast.success("Lead toegevoegd");
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Toevoegen mislukt");
        } finally { setSaving(false); }
    };
    return (
        <Modal title="Nieuwe lead" onClose={onClose}>
            <form onSubmit={submit} className="space-y-3.5" data-testid="add-lead-form">
                <FormField label="E-mail *" required>
                    <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={fieldCss} data-testid="add-lead-email" />
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                    <FormField label="Naam">
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Marc De Vries" className={fieldCss} data-testid="add-lead-name" />
                    </FormField>
                    <FormField label="Kantoor">
                        <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Immo De Vries" className={fieldCss} data-testid="add-lead-company" />
                    </FormField>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <FormField label="Telefoon">
                        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+32 …" className={fieldCss} data-testid="add-lead-phone" />
                    </FormField>
                    <FormField label="Stad">
                        <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Gent" className={fieldCss} data-testid="add-lead-city" />
                    </FormField>
                </div>
                <FormField label="Website">
                    <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="immo-devries.be" className={fieldCss} data-testid="add-lead-website" />
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                    <FormField label="Taal voor mail">
                        <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className={fieldCss} data-testid="add-lead-language">
                            <option value="nl">Nederlands</option>
                            <option value="fr">Français</option>
                            <option value="en">English</option>
                            <option value="es">Español</option>
                        </select>
                    </FormField>
                    <FormField label="Bron">
                        <input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="LinkedIn, BIV, …" className={fieldCss} data-testid="add-lead-source" />
                    </FormField>
                </div>
                <div className="flex gap-2 pt-2">
                    <button type="button" onClick={onClose} className="flex-1 h-10 rounded-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-100 text-sm">Annuleren</button>
                    <button type="submit" disabled={saving} className="flex-1 h-10 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-sm active:scale-[0.98] inline-flex items-center justify-center gap-2" data-testid="add-lead-submit">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Toevoegen
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function EditLeadModal({ lead, onClose }) {
    const [form, setForm] = useState({
        name: lead.name || "", company: lead.company || "", phone: lead.phone || "",
        website: lead.website || "", city: lead.city || "", source: lead.source || "",
        notes: lead.notes || "", language: lead.language || "nl", status: lead.status || "new",
    });
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put(`/admin/leads/${lead.id}`, form);
            toast.success("Lead bijgewerkt");
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Opslaan mislukt");
        } finally { setSaving(false); }
    };
    return (
        <Modal title={`Bewerk ${lead.email}`} onClose={onClose}>
            <form onSubmit={submit} className="space-y-3.5" data-testid="edit-lead-form">
                <div className="grid grid-cols-2 gap-3">
                    <FormField label="Naam"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCss} /></FormField>
                    <FormField label="Kantoor"><input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={fieldCss} /></FormField>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <FormField label="Telefoon"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={fieldCss} /></FormField>
                    <FormField label="Stad"><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={fieldCss} /></FormField>
                </div>
                <FormField label="Website"><input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={fieldCss} /></FormField>
                <div className="grid grid-cols-2 gap-3">
                    <FormField label="Taal">
                        <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className={fieldCss}>
                            <option value="nl">Nederlands</option><option value="fr">Français</option><option value="en">English</option><option value="es">Español</option>
                        </select>
                    </FormField>
                    <FormField label="Status">
                        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={fieldCss}>
                            {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                        </select>
                    </FormField>
                </div>
                <FormField label="Bron"><input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className={fieldCss} /></FormField>
                <FormField label="Notities (intern)">
                    <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className={`${fieldCss} resize-none`} />
                </FormField>
                <div className="flex gap-2 pt-2">
                    <button type="button" onClick={onClose} className="flex-1 h-10 rounded-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-100 text-sm">Annuleren</button>
                    <button type="submit" disabled={saving} className="flex-1 h-10 rounded-sm bg-white hover:bg-zinc-100 text-zinc-950 font-bold text-sm active:scale-[0.98] inline-flex items-center justify-center gap-2" data-testid="edit-lead-submit">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Opslaan
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function BulkLeadsModal({ onClose }) {
    const [text, setText] = useState("");
    const [language, setLanguage] = useState("nl");
    const [leadType, setLeadType] = useState("agency");
    const [importing, setImporting] = useState(false);
    const fileRef = useRef(null);
    const parsed = useMemo(() => parseBulk(text), [text]);
    const submit = async () => {
        if (parsed.length === 0) return toast.error("Geen geldige leads gevonden");
        setImporting(true);
        try {
            const payload = { leads: parsed.map((p) => ({ ...p, language, lead_type: leadType })) };
            const { data } = await api.post("/admin/leads/bulk", payload);
            toast.success(`${data.added} toegevoegd · ${data.skipped} duplicaten genegeerd`);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import mislukt");
        } finally { setImporting(false); }
    };
    const uploadFile = async (file) => {
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("lead_type", leadType);
            fd.append("language", language);
            const { data } = await api.post("/admin/leads/import-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${data.added} toegevoegd · ${data.skipped} duplicaten/uitgeschreven genegeerd (${data.parsed} herkend)`);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bestand importeren mislukt");
        } finally { setImporting(false); }
    };
    return (
        <Modal title="Bulk import" onClose={onClose}>
            {/* Doelgroep + taal gelden voor beide importmethodes */}
            <div className="flex items-center gap-2 mb-4">
                <select value={leadType} onChange={(e) => setLeadType(e.target.value)} className={`${fieldCss} h-9`} data-testid="bulk-leadtype-select">
                    <option value="agency">🏠 Makelaars</option>
                    <option value="photographer">📸 Vastgoedfotografen</option>
                    <option value="tenerife">🌴 Tenerife makelaars</option>
                </select>
                <select value={language} onChange={(e) => setLanguage(e.target.value)} className={`${fieldCss} h-9`}>
                    <option value="nl">Mail-taal: Nederlands</option>
                    <option value="fr">Mail-taal: Français</option>
                    <option value="en">Mail-taal: English</option>
                    <option value="es">Mail-taal: Español</option>
                </select>
            </div>
            {/* Optie 1: Excel/CSV bestand */}
            <div className="border border-dashed border-zinc-300 rounded-sm p-4 mb-4 text-center bg-zinc-50">
                <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ""; }}
                    data-testid="bulk-file-input"
                />
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={importing}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-sm active:scale-[0.98] disabled:opacity-50"
                    data-testid="bulk-file-btn"
                >
                    {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Excel of CSV uploaden
                </button>
                <p className="text-[11px] text-zinc-500 mt-2">Kolommen worden automatisch herkend (naam, e-mail, telefoon, plaats, website…)</p>
            </div>
            <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
                Of plak je leads — één per regel. Ondersteunde formaten (komma-gescheiden, in willekeurige volgorde):<br />
                <span className="text-zinc-500">
                    <code>email, naam, kantoor, telefoon, website, stad</code>
                </span>
            </p>
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={10}
                placeholder="marc@immo-devries.be, Marc De Vries, Immo De Vries, +32 9 ..., immo-devries.be, Gent
sophie@vlaminck.be, Sophie Vlaminck, , , , Brugge
info@kantoor.be"
                className={`${fieldCss} resize-none font-mono text-xs`}
                data-testid="bulk-leads-textarea"
            />
            <div className="flex items-center justify-between mt-3">
                <span className="text-[11px] text-zinc-500" data-testid="bulk-parsed-count">
                    {parsed.length} geldige lead{parsed.length === 1 ? "" : "s"} herkend
                </span>
            </div>
            <div className="flex gap-2 pt-4">
                <button type="button" onClick={onClose} className="flex-1 h-10 rounded-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-100 text-sm">Annuleren</button>
                <button type="button" onClick={submit} disabled={importing || parsed.length === 0} className="flex-1 h-10 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-sm active:scale-[0.98] inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="bulk-leads-submit">
                    {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importeer {parsed.length > 0 ? `${parsed.length} leads` : ""}
                </button>
            </div>
        </Modal>
    );
}

function Modal({ title, children, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <div className="relative w-full max-w-xl bg-white border border-zinc-200 rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
                    <h2 className="font-display text-lg font-bold tracking-tight">{title}</h2>
                    <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900"><X className="h-5 w-5" /></button>
                </div>
                <div className="p-5 max-h-[78vh] overflow-y-auto">{children}</div>
            </div>
        </div>
    );
}

function FormField({ label, required, children }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 font-bold">
                {label}
            </label>
            {children}
        </div>
    );
}

const fieldCss = "w-full h-10 bg-white border border-zinc-200 focus:border-[#E5A93D]/60 outline-none rounded-sm px-3 text-sm text-zinc-950 placeholder:text-zinc-400";

/**
 * Parses comma/space/tab separated pasted text into lead objects.
 * The FIRST field that looks like a valid email becomes the `email`; the
 * remaining fields are mapped — best-effort — to name/company/phone/website/city
 * in declaration order. Empty or invalid rows are silently dropped.
 */
function parseBulk(text) {
    const emailRx = /^[\w.+-]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/i;
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const out = [];
    for (const line of lines) {
        const parts = line.split(/[,;\t]/).map((p) => p.trim()).filter(Boolean);
        // Find the first email-looking token
        const emailIdx = parts.findIndex((p) => emailRx.test(p));
        if (emailIdx === -1) continue;
        const email = parts[emailIdx];
        // Anything else is mapped by positional convention: name, company,
        // phone, website, city. We drop the email out and keep the rest.
        const rest = parts.filter((_, i) => i !== emailIdx);
        const [name, company, phone, website, city] = rest;
        out.push({
            email,
            name: name || undefined,
            company: company || undefined,
            phone: phone || undefined,
            website: website || undefined,
            city: city || undefined,
        });
    }
    return out;
}


function LeadsStatsWidget({ stats, onRefresh }) {
    const [runningDrip, setRunningDrip] = useState(false);
    const [runningPoll, setRunningPoll] = useState(false);
    const maxDay = Math.max(1, ...(stats.by_day || []).map((d) => d.sent));
    const runDrip = async () => {
        setRunningDrip(true);
        try {
            await api.post("/admin/leads/drip/run-now");
            toast.success("Drip-batch gestart — vernieuwen…");
            setTimeout(() => { onRefresh?.(); }, 800);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Drip mislukt");
        } finally { setRunningDrip(false); }
    };
    const runPoll = async () => {
        setRunningPoll(true);
        try {
            const { data } = await api.post("/admin/leads/imap/poll-now");
            toast.success(`IMAP poll klaar · ${data.last_imap_match_count || 0} nieuwe reply(s)`);
            onRefresh?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "IMAP poll mislukt");
        } finally { setRunningPoll(false); }
    };
    return (
        <div className="mb-8 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="leads-stats-widget">
            <StatTile
                label="Intro's vandaag"
                value={stats.sent_today}
                hint={`van ${stats.daily_limit}/dag (${stats.daily_limit_agency ?? "?"} mak. + ${stats.daily_limit_photog ?? "?"} foto. + ${stats.daily_limit_tenerife ?? "?"} Tenerife) · ${stats.remaining_today} resterend`}
                accent="amber"
                progress={stats.daily_limit ? (stats.sent_today / stats.daily_limit) * 100 : 0}
            />
            <StatTile
                label="Follow-ups vandaag"
                value={stats.sent_today_followup ?? 0}
                hint={`van ${stats.followup_daily_limit ?? 50}/dag · ${stats.remaining_today_followup ?? 0} resterend`}
                accent="default"
                progress={stats.followup_daily_limit ? ((stats.sent_today_followup ?? 0) / stats.followup_daily_limit) * 100 : 0}
            />
            <StatTile
                label="Replies"
                value={stats.totals.replied}
                hint={`${stats.reply_rate_pct}% reply-rate · ${stats.totals.unsubscribed} uitgeschreven`}
                accent="emerald"
            />
            <StatTile
                label="ETA"
                value={`${stats.days_to_finish}d`}
                hint={stats.drip_enabled ? `Drip AAN · ${stats.totals.new} nieuw` : "Drip uit · zet aan in mailbox-instellingen"}
                accent={stats.drip_enabled ? "green" : "muted"}
            />
            {/* 7-day sparkline */}
            <div className="md:col-span-4 bg-white border border-zinc-200 rounded-sm p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">
                        Verzendingen · laatste 7 dagen
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={runPoll}
                            disabled={runningPoll}
                            data-testid="leads-poll-now"
                            className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-sm border border-zinc-200 text-[11px] text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100 disabled:opacity-50"
                            title="IMAP nu pollen voor replies"
                        >
                            {runningPoll ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageCircle className="h-3 w-3" />}
                            Replies ophalen
                        </button>
                        <button
                            type="button"
                            onClick={runDrip}
                            disabled={runningDrip || (stats.remaining_today === 0 && (stats.remaining_today_followup ?? 0) === 0)}
                            data-testid="leads-drip-now"
                            className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-sm bg-[#E5A93D]/15 border border-[#E5A93D]/40 text-[#E5A93D] hover:bg-[#E5A93D]/25 text-[11px] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                            title={(stats.remaining_today === 0 && (stats.remaining_today_followup ?? 0) === 0) ? "Beide daglimieten bereikt" : "Stuur nu de batch van vandaag"}
                        >
                            {runningDrip ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                            Stuur batch nu
                        </button>
                    </div>
                </div>
                <div className="flex items-end gap-2 h-20">
                    {(stats.by_day || []).map((d) => {
                        const pct = (d.sent / maxDay) * 100;
                        const isToday = d.date === stats.by_day[stats.by_day.length - 1].date;
                        return (
                            <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1.5">
                                <div className={`text-[10px] font-bold ${isToday ? "text-[#E5A93D]" : "text-zinc-500"}`}>{d.sent}</div>
                                <div
                                    className={`w-full rounded-sm transition-all ${isToday ? "bg-[#E5A93D]" : "bg-zinc-200"}`}
                                    style={{ height: `${Math.max(4, pct)}%` }}
                                />
                                <div className="text-[9px] text-zinc-400 uppercase">
                                    {new Date(d.date).toLocaleDateString("nl-BE", { weekday: "short" }).replace(".", "")}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function StatTile({ label, value, hint, accent = "default", progress }) {
    const accentClasses = {
        amber: "border-amber-500/30 bg-amber-500/[0.04]",
        emerald: "border-emerald-500/30 bg-emerald-500/[0.04]",
        green: "border-emerald-500/40 bg-emerald-500/[0.06]",
        muted: "border-zinc-200 bg-white",
        default: "border-zinc-200 bg-white",
    }[accent] || "border-zinc-200 bg-white";
    const valueColor = accent === "amber" ? "text-amber-700" : accent === "emerald" || accent === "green" ? "text-emerald-700" : "text-zinc-950";
    return (
        <div className={`rounded-sm border ${accentClasses} p-4`}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-2">{label}</div>
            <div className={`font-display text-3xl font-bold tracking-tight ${valueColor}`}>{value}</div>
            <div className="mt-1 text-[11px] text-zinc-500 leading-tight">{hint}</div>
            {progress != null && (
                <div className="mt-3 h-1 w-full bg-zinc-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#E5A93D] transition-all" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
                </div>
            )}
        </div>
    );
}
